import { type RefObject, useEffect, useRef } from 'react';

/** Marks a pill's remove button so `useAppliedFilterRemoveFocus` can find it. */
export const APPLIED_FILTER_REMOVE_ATTR = 'data-applied-filter-remove';

// Long enough for a URL-backed filter change to render; a removal the parent ignored expires instead of moving focus later.
const PENDING_TTL_MS = 1_000;

const removeButtons = (row: HTMLElement | null) =>
  Array.from(row?.querySelectorAll<HTMLElement>(`[${APPLIED_FILTER_REMOVE_ATTR}]`) ?? []);

/**
 * Keeps keyboard focus in place when a filter pill is removed: on the next pill's remove button, else the previous
 * one, else `fallbackRef`. Attach `rowRef` to the element holding the pills and call `beforeRemove` before removing.
 * Does nothing unless focus was on a remove button, which includes a mouse click in browsers that focus buttons on
 * click (Chrome, Firefox outside macOS).
 */
export function useAppliedFilterRemoveFocus<T extends HTMLElement = HTMLDivElement>(
  fallbackRef?: RefObject<HTMLElement | null>,
) {
  const rowRef = useRef<T>(null);
  const pending = useRef<{ button: HTMLElement; index: number; count: number; at: number } | null>(null);

  // No deps: the parent may apply the removal a render or more later, so every render checks for it.
  useEffect(() => {
    const target = pending.current;
    if (!target) return;
    const active = document.activeElement;
    const focusMovedOn = active !== target.button && active !== document.body && active !== null;
    if (focusMovedOn || Date.now() - target.at > PENDING_TTL_MS) {
      pending.current = null;
      return;
    }
    const buttons = removeButtons(rowRef.current);
    if (buttons.length >= target.count) return;
    pending.current = null;
    (buttons[Math.min(target.index, buttons.length - 1)] ?? fallbackRef?.current)?.focus();
  });

  const beforeRemove = () => {
    const buttons = removeButtons(rowRef.current);
    const index = buttons.indexOf(document.activeElement as HTMLElement);
    pending.current = index === -1 ? null : { button: buttons[index], index, count: buttons.length, at: Date.now() };
  };

  return { rowRef, beforeRemove };
}
